import { useRef, useState } from "react";
import { Loader2, Paperclip, Send, Smile, X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MAX_FILE, capi, errMsg, fsize, uploadFile } from "./chatApi";

const EMOJI = ["👍", "❤️", "😂", "😮", "🙏", "🎉", "🔥", "✅"];

export const Composer = ({ channel, threadId, people, onSent, placeholder, testid = "chat", partnerQid }) => {
  const [text, setText] = useState("");
  const [toPartner, setToPartner] = useState(false);
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [mq, setMq] = useState(null);
  const ref = useRef(null);
  const fileRef = useRef(null);
  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const m = v.slice(0, e.target.selectionStart).match(/@([\w.]*)$/);
    setMq(m ? m[1].toLowerCase() : null);
  };
  const pickPerson = (p) => {
    const at = ref.current?.selectionStart ?? text.length;
    setText((t) => t.slice(0, at).replace(/@([\w.]*)$/, `@${p.name} `) + t.slice(at));
    setMentions((x) => [...new Set([...x, p.id])]);
    setMq(null); ref.current?.focus();
  };
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_FILE) { toast.error("Files can be up to 50 MB"); return; }
    setFile(f);
  };
  const send = async () => {
    if (!text.trim() && !file) return;
    try {
      const hub = partnerQid && toPartner;
      if (hub && file && (!/^image\/|pdf$/.test(file.type) || file.size > 10 * 1048576)) { toast.error("The partner can only get photos or PDFs up to 10 MB"); return; }
      if (hub) {
        if (file) {
          const fd = new FormData(); fd.append("files", file); if (text.trim()) fd.append("text", text.trim());
          await capi("post", `/order-register/queries/${partnerQid}/files`, fd);
        } else await capi("post", `/order-register/queries/${partnerQid}/note`, { text: text.trim() });
      }
      let meta = null;
      if (file) { setPct(0); meta = await uploadFile(file, setPct); }
      const keep = mentions.filter((id) => text.includes(`@${people.find((p) => p.id === id)?.name}`));
      await capi("post", "/chat/messages", { channel, thread_id: threadId, message: text, mentions: keep, file: meta, to_partner: !!hub });
      if (hub) toast.success("Sent to the partner's Hub too");
      setText(""); setFile(null); setMentions([]); setToPartner(false); onSent?.();
    } catch (e) { toast.error(errMsg(e)); }
    setPct(null);
  };
  const hits = mq === null ? [] : people.filter((p) => p.name?.toLowerCase().includes(mq)).slice(0, 6);
  return (
    <div className="relative border-t bg-white p-3">
      {hits.length > 0 && <div className="absolute bottom-full left-3 z-20 mb-1 w-64 overflow-hidden rounded-lg border bg-white shadow-lg" data-testid={`${testid}-mention-list`}>
        {hits.map((p) => <button key={p.id} onClick={() => pickPerson(p)} className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-indigo-50" data-testid={`${testid}-mention-${p.id}`}><span>{p.name}</span><span className="text-[11px] text-slate-400">{p.team}</span></button>)}</div>}
      {file && <div className="mb-2 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-xs" data-testid={`${testid}-file-chip`}>
        <Paperclip className="h-3.5 w-3.5" /><span className="truncate">{file.name}</span><span className="text-slate-400">{fsize(file.size)}</span>
        {pct !== null ? <span className="ml-auto font-semibold text-indigo-600" data-testid={`${testid}-upload-pct`}>{pct}%</span> : <button onClick={() => setFile(null)} className="ml-auto"><X className="h-3.5 w-3.5" /></button>}</div>}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" className="hidden" onChange={pickFile} data-testid={`${testid}-file-input`} />
        <button onClick={() => fileRef.current?.click()} title="Attach (up to 50 MB)" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" data-testid={`${testid}-attach-btn`}><Paperclip className="h-5 w-5" /></button>
        <Popover><PopoverTrigger asChild><button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Smile className="h-5 w-5" /></button></PopoverTrigger>
          <PopoverContent className="flex w-auto gap-1 p-2">{EMOJI.map((e) => <button key={e} onClick={() => setText((t) => t + e)} className="rounded p-1 text-lg hover:bg-slate-100">{e}</button>)}</PopoverContent></Popover>
        <textarea ref={ref} value={text} onChange={onChange} rows={1} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !hits.length) { e.preventDefault(); send(); } }}
          className="max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30" data-testid={`${testid}-input`} />
        <button onClick={send} disabled={pct !== null || (!text.trim() && !file)} className="rounded-lg bg-indigo-600 p-2.5 text-white transition-colors hover:bg-indigo-700 disabled:opacity-40" data-testid={`${testid}-send-btn`}>
          {pct !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
      </div>
      {partnerQid && <label className={`mt-1.5 flex w-fit cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold ${toPartner ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700"}`} data-testid={`${testid}-to-partner`}>
        <input type="checkbox" checked={toPartner} onChange={(e) => setToPartner(e.target.checked)} data-testid={`${testid}-to-partner-check`} />Send to partner's Hub {file && "(photo / PDF)"}</label>}
      <p className="mt-1 text-[10px] text-slate-400">Enter to send · Shift+Enter new line · @ to mention · files up to 50 MB</p>
    </div>
  );
};
